// Assessment dimensions, industries, company sizes, and score helpers
// Extracted from pages/Assessment.jsx for better maintainability

const getAssessmentDimensions = (language) => [
  {
    id: 'people',
    title: language === 'nl' ? 'Mensen & Change' : 'People & Change',
    description: language === 'nl' 
      ? 'Deze vragen belichten de menselijke kant van de AI-transitie.'
      : 'These questions highlight the human side of the AI transition.',
    intro: language === 'nl'
      ? '👋 Laten we starten! Deze vragen gaan over jullie team - vaak de leukste om te beantwoorden.'
      : '👋 Let\'s get started! These questions are about your team - often the most fun to answer.',
    questions: [
      {
        id: 16,
        text_en: 'To what extent does the HR team see the benefits of implementing and using AI tools in HR?',
        text_nl: 'In welke mate ziet het HR-team de voordelen van de implementatie en het gebruik van AI-tools in HR?',
        tooltip_en: 'Awareness of WHY change is needed is the first step in any transformation.',
        tooltip_nl: 'Bewustzijn van WAAROM verandering nodig is, is de eerste stap in elke transformatie.',
      },
      {
        id: 17,
        text_en: 'To what extent does the individual HR employee believe AI will help them work more efficiently?',
        text_nl: 'In welke mate gelooft de individuele HR-medewerker dat AI hem of haar helpt om efficiënter te kunnen werken?',
        tooltip_en: '"What\'s in it for me?" - Personal motivation is crucial for adoption.',
        tooltip_nl: '"What\'s in it for me?" - Persoonlijke motivatie is cruciaal voor adoptie.',
      },
      {
        id: 18,
        text_en: 'To what extent is there enthusiasm within the team to experiment with AI in a controlled manner?',
        text_nl: 'In welke mate is er enthousiasme binnen het team om gecontroleerd te experimenteren met AI?',
        tooltip_en: 'A culture of controlled experimentation accelerates learning and adoption.',
        tooltip_nl: 'Een cultuur van gecontroleerd experimenteren versnelt leren en adoptie.',
      },
      {
        id: 19,
        text_en: 'To what extent have HR employees received basic training on AI in HR?',
        text_nl: 'In welke mate hebben HR-medewerkers een basistraining over AI in HR gehad?',
        tooltip_en: 'Training doesn\'t need to be technical - understanding concepts and applications is key.',
        tooltip_nl: 'Training hoeft niet technisch te zijn - begrip van concepten en toepassingen is essentieel.',
      },
      {
        id: 20,
        text_en: 'To what extent are HR employees able to apply AI consistently in their daily work?',
        text_nl: 'In welke mate lukt het HR-medewerkers om AI op een consistente manier toe te passen in hun dagelijks werk?',
        tooltip_en: 'The gap between knowing and doing is often larger than expected.',
        tooltip_nl: 'De kloof tussen weten en doen is vaak groter dan verwacht.',
      },
      {
        id: 22,
        text_en: 'To what extent do HR employees have time and space to practice with AI tools?',
        text_nl: 'In welke mate hebben HR-medewerkers tijd en ruimte om te oefenen met AI-tools?',
        tooltip_en: 'Learning requires dedicated time, not just added to existing workload.',
        tooltip_nl: 'Leren vereist toegewijde tijd, niet alleen toegevoegd aan bestaande werkdruk.',
      },
      {
        id: 23,
        text_en: 'To what extent are good AI practices made visible and shared within the team?',
        text_nl: 'In welke mate worden goede AI-praktijken zichtbaar gemaakt en gedeeld binnen het team?',
        tooltip_en: 'Celebrating successes and sharing learnings sustains momentum.',
        tooltip_nl: 'Successen vieren en learnings delen houdt het momentum vast.',
      },
      {
        id: 24,
        text_en: 'To what extent is the quality and effectiveness of AI applications monitored for HR?',
        text_nl: 'In welke mate wordt de kwaliteit en effectiviteit van AI-toepassingen voor HR gemonitord?',
        tooltip_en: 'Ongoing monitoring ensures AI continues to deliver value and catches issues early.',
        tooltip_nl: 'Monitoring zorgt dat AI waarde blijft leveren en vangt problemen vroeg op.',
      },
    ],
  },
  {
    id: 'governance',
    title: 'Governance',
    description: language === 'nl' 
      ? 'Deze vragen behandelen het kader rondom AI-gebruik: beleid, ethiek, verantwoordelijkheden en wetgeving.'
      : 'These questions address the framework around AI use: policy, ethics, responsibilities, and legislation.',
    intro: language === 'nl'
      ? '💡 Tip: De meeste organisaties scoren hier al goed! Dit gaat over hoe jullie omgaan met nieuwe technologie.'
      : '💡 Tip: Most organizations already score well here! This is about how you handle new technology.',
    questions: [
      {
        id: 6,
        text_en: 'To what extent are you familiar with relevant AI legislation such as the AI Act and GDPR?',
        text_nl: 'In welke mate bent u bekend met relevante AI-wetgeving zoals de AI Act en AVG/GDPR?',
        tooltip_en: 'The EU AI Act classifies HR applications as "high-risk" with specific requirements.',
        tooltip_nl: 'De EU AI Act classificeert HR-toepassingen als "hoog-risico" met specifieke vereisten.',
      },
      {
        id: 7,
        text_en: 'To what extent does your organization have a clear AI policy with guidelines for responsible use?',
        text_nl: 'In welke mate heeft uw organisatie een duidelijk AI-beleid met richtlijnen voor verantwoord gebruik?',
        tooltip_en: 'This includes do\'s and don\'ts, approved tools, and escalation procedures.',
        tooltip_nl: 'Dit omvat do\'s en don\'ts, goedgekeurde tools en escalatieprocedures.',
      },
      {
        id: 8,
        text_en: 'To what extent is it clear which AI tools may and may not be used within your organization?',
        text_nl: 'In welke mate is het duidelijk welke AI-tools wel en niet mogen worden gebruikt binnen uw organisatie?',
        tooltip_en: 'Think about approved tool lists, shadow IT policies, and security guidelines.',
        tooltip_nl: 'Denk aan goedgekeurde toollijsten, shadow IT-beleid en beveiligingsrichtlijnen.',
      },
      {
        id: 9,
        text_en: 'To what extent is "human in the loop" important for AI decisions with HR impact?',
        text_nl: 'In welke mate is "human in the loop" belangrijk bij AI-beslissingen met HR-impact?',
        tooltip_en: 'Critical decisions (hiring, firing, promotions) should always have human oversight.',
        tooltip_nl: 'Kritische beslissingen (aanname, ontslag, promoties) moeten altijd menselijk toezicht hebben.',
      },
      {
        id: 11,
        text_en: 'To what extent is there a dedicated AI budget available for HR initiatives?',
        text_nl: 'In welke mate is er een specifiek AI-budget beschikbaar voor HR-initiatieven?',
        tooltip_en: 'A dedicated budget signals organizational commitment and enables planning.',
        tooltip_nl: 'Een specifiek budget signaleert organisatorische betrokkenheid en maakt planning mogelijk.',
      },
    ],
  },
  {
    id: 'strategy',
    title: language === 'nl' ? 'Strategie' : 'Strategy',
    description: language === 'nl' 
      ? 'Deze vragen peilen naar de mate waarin AI is verankerd in uw bedrijfs- en HR-strategie.'
      : 'These questions explore the extent to which AI is embedded in your business and HR strategy.',
    intro: language === 'nl'
      ? '🎯 Dit gaat over visie en planning - er zijn geen foute antwoorden!'
      : '🎯 This is about vision and planning - there are no wrong answers!',
    questions: [
      {
        id: 1,
        text_en: 'To what extent does your organization have a clear AI vision as part of its overall business strategy?',
        text_nl: 'In welke mate heeft uw organisatie een duidelijke AI-visie als onderdeel van de algemene bedrijfsstrategie?',
        tooltip_en: 'Think about whether AI is mentioned in strategic documents, board discussions, or company-wide communications.',
        tooltip_nl: 'Denk na of AI wordt genoemd in strategische documenten, bestuursvergaderingen of bedrijfsbrede communicatie.',
      },
      {
        id: 2,
        text_en: 'To what extent does your HR department have its own AI strategy or roadmap?',
        text_nl: 'In welke mate heeft uw HR-afdeling een eigen AI-strategie of roadmap?',
        tooltip_en: 'This may differ from the overall company strategy. HR might be ahead or behind.',
        tooltip_nl: 'Dit kan verschillen van de algemene bedrijfsstrategie. HR kan vooroplopen of achterlopen.',
      },
      {
        id: 3,
        text_en: 'To what extent have you already identified concrete AI use cases for HR?',
        text_nl: 'In welke mate heeft u reeds concrete AI use cases voor HR geïdentificeerd?',
        tooltip_en: 'Examples: CV screening, chatbots for FAQ, predictive analytics for turnover.',
        tooltip_nl: 'Voorbeelden: CV-screening, chatbots voor FAQ, voorspellende analyses voor verloop.',
      },
      {
        id: 4,
        text_en: 'To what extent are AI initiatives within HR aligned with overall business objectives?',
        text_nl: 'In welke mate zijn AI-initiatieven binnen HR afgestemd op de algemene bedrijfsdoelstellingen?',
        tooltip_en: 'AI projects should support broader organizational goals, not exist in isolation.',
        tooltip_nl: 'AI-projecten moeten bredere organisatiedoelen ondersteunen, niet op zichzelf staan.',
      },
      {
        id: 5,
        text_en: 'To what extent is there executive sponsorship for AI initiatives within HR?',
        text_nl: 'In welke mate is er leiderschapssteun voor AI-initiatieven binnen HR?',
        tooltip_en: 'Executive buy-in is critical for resources, priority, and organizational change.',
        tooltip_nl: 'Steun van het management is cruciaal voor middelen, prioriteit en organisatieverandering.',
      },
    ],
  },
  {
    id: 'technology',
    title: language === 'nl' ? 'Technologie & Data' : 'Technology & Data',
    description: language === 'nl' 
      ? 'Deze vragen focussen op de fundamenten: datakwaliteit, toegankelijkheid en gereedheid voor AI.'
      : 'These questions focus on the fundamentals: data quality, accessibility, and readiness for AI.',
    intro: language === 'nl'
      ? '🔧 Bijna klaar! Nog 2 korte secties te gaan.'
      : '🔧 Almost there! Just 2 short sections to go.',
    questions: [
      {
        id: 12,
        text_en: 'To what extent is your employee data centralized and easily accessible?',
        text_nl: 'In welke mate is uw medewerkersdata gecentraliseerd en gemakkelijk toegankelijk?',
        tooltip_en: 'Fragmented data across multiple systems is the #1 barrier to successful AI implementation.',
        tooltip_nl: 'Gefragmenteerde data over meerdere systemen is de #1 barrière voor succesvolle AI-implementatie.',
      },
      {
        id: 13,
        text_en: 'To what extent is your HR data of high quality and reliable?',
        text_nl: 'In welke mate is uw HR-data van hoge kwaliteit en betrouwbaar?',
        tooltip_en: 'AI is only as good as the data it\'s trained on. "Garbage in, garbage out."',
        tooltip_nl: 'AI is slechts zo goed als de data waarop het is getraind. "Garbage in, garbage out."',
      },
      {
        id: 14,
        text_en: 'To what extent do you think your HR processes are ready for AI digitalization?',
        text_nl: 'In welke mate denkt u dat uw HR-processen klaar zijn voor AI-digitalisering?',
        tooltip_en: 'Consider standardization, documentation, and digital maturity of current processes.',
        tooltip_nl: 'Overweeg standaardisatie, documentatie en digitale volwassenheid van huidige processen.',
      },
      {
        id: 15,
        text_en: 'To what extent are your HR systems integrated with other business platforms?',
        text_nl: 'In welke mate zijn uw HR-systemen geïntegreerd met andere bedrijfsplatformen?',
        tooltip_en: 'Integration enables data flow and more comprehensive AI insights.',
        tooltip_nl: 'Integratie maakt datastromen en meer uitgebreide AI-inzichten mogelijk.',
      },
    ],
  },
  {
    id: 'qualification',
    title: language === 'nl' ? 'Over uw situatie' : 'About Your Situation',
    description: language === 'nl' 
      ? 'Tot slot enkele vragen om uw specifieke situatie beter te begrijpen.'
      : 'Finally, some questions to better understand your specific situation.',
    isQualification: true,
    questions: [
      {
        id: 25,
        text_en: 'Who is typically involved in decisions about HR technology and AI within your organization?',
        text_nl: 'Wie is doorgaans betrokken bij beslissingen over HR-technologie en AI binnen uw organisatie?',
        tooltip_en: 'Understanding decision-making helps us provide relevant information.',
        tooltip_nl: 'Inzicht in besluitvorming helpt ons relevante informatie te geven.',
        type: 'select',
        options_en: [
          { value: 'me', label: 'I decide independently' },
          { value: 'team', label: 'HR leadership team' },
          { value: 'it', label: 'Together with IT' },
          { value: 'executive', label: 'Executive/board level' },
          { value: 'other', label: 'Other' },
        ],
        options_nl: [
          { value: 'me', label: 'Ik beslis zelfstandig' },
          { value: 'team', label: 'HR-leiderschapsteam' },
          { value: 'it', label: 'Samen met IT' },
          { value: 'executive', label: 'Directie/bestuursniveau' },
          { value: 'other', label: 'Anders' },
        ],
      },
      {
        id: 26,
        text_en: 'What is your biggest challenge or priority regarding AI in HR right now?',
        text_nl: 'Wat is uw grootste uitdaging of prioriteit met betrekking tot AI in HR op dit moment?',
        tooltip_en: 'This helps us tailor our recommendations to your needs. You can select multiple options.',
        tooltip_nl: 'Dit helpt ons onze aanbevelingen af te stemmen op uw behoeften. U kunt meerdere opties selecteren.',
        type: 'multiselect',
        options_en: [
          { value: 'awareness', label: 'Creating awareness and buy-in' },
          { value: 'usecases', label: 'Identifying the right use cases' },
          { value: 'implementation', label: 'Implementing AI solutions' },
          { value: 'skills', label: 'Building AI skills in the team' },
          { value: 'governance', label: 'Setting up governance and policies' },
          { value: 'budget', label: 'Securing budget' },
          { value: 'other', label: 'Other' },
        ],
        options_nl: [
          { value: 'awareness', label: 'Bewustzijn en draagvlak creëren' },
          { value: 'usecases', label: 'de juiste use cases identificeren' },
          { value: 'implementation', label: 'AI-oplossingen implementeren' },
          { value: 'skills', label: 'AI-vaardigheden opbouwen in het team' },
          { value: 'governance', label: 'Governance en beleid opzetten' },
          { value: 'budget', label: 'Budget veiligstellen' },
          { value: 'other', label: 'Anders' },
        ],
      },
      {
        id: 27,
        text_en: 'Within what timeframe would you like to see a rollout of AI within HR?',
        text_nl: 'Binnen welk tijdsbestek zou u een uitrol van AI binnen HR willen zien?',
        tooltip_en: 'This helps us understand your urgency and planning horizon.',
        tooltip_nl: 'Dit helpt ons uw urgentie en planningshorizon te begrijpen.',
        type: 'select',
        options_en: [
          { value: '1', label: 'Within 1 month' },
          { value: '3', label: 'Within 3 months' },
          { value: '6', label: 'Within 6 months' },
          { value: '12', label: 'Within 12 months' },
          { value: '18', label: 'Within 18 months' },
          { value: 'unknown', label: 'No idea' },
        ],
        options_nl: [
          { value: '1', label: 'Binnen de maand' },
          { value: '3', label: 'Binnen 3 maanden' },
          { value: '6', label: 'Binnen 6 maanden' },
          { value: '12', label: 'Binnen 12 maanden' },
          { value: '18', label: 'Binnen 18 maanden' },
          { value: 'unknown', label: 'Geen idee' },
        ],
      },
      {
        id: 28,
        text_en: 'How interested are you in a free consultation to discuss your AI for HR results and ROI of use case(s)?',
        text_nl: 'Hoe geïnteresseerd bent u in een gratis gesprek om uw AI-maturiteitsresultaten en ROI van use case(s) te bespreken?',
        tooltip_en: 'No obligation - just a conversation to explore how we might help.',
        tooltip_nl: 'Geen verplichting - gewoon een gesprek om te verkennen hoe we kunnen helpen.',
        type: 'select',
        options_en: [
          { value: 'very', label: 'Very interested - please contact me' },
          { value: 'somewhat', label: 'Somewhat interested - send me more info first' },
          { value: 'later', label: 'Not now, but maybe later' },
          { value: 'no', label: 'No, just the results please' },
        ],
        options_nl: [
          { value: 'very', label: 'Zeer geïnteresseerd - neem contact met mij op' },
          { value: 'somewhat', label: 'Enigszins geïnteresseerd - stuur me eerst meer info' },
          { value: 'later', label: 'Niet nu, maar misschien later' },
          { value: 'no', label: 'Nee, alleen de resultaten graag' },
        ],
      },
    ],
  },
];

const getIndustries = (language) => [
  { value: 'technology', label: language === 'nl' ? 'Technologie' : 'Technology' },
  { value: 'finance', label: language === 'nl' ? 'Financiële diensten' : 'Financial Services' },
  { value: 'healthcare', label: language === 'nl' ? 'Gezondheidszorg' : 'Healthcare' },
  { value: 'manufacturing', label: language === 'nl' ? 'Productie' : 'Manufacturing' },
  { value: 'retail', label: language === 'nl' ? 'Retail' : 'Retail' },
  { value: 'education', label: language === 'nl' ? 'Onderwijs' : 'Education' },
  { value: 'government', label: language === 'nl' ? 'Overheid' : 'Government' },
  { value: 'consulting', label: language === 'nl' ? 'Consulting' : 'Consulting' },
  { value: 'logistics', label: language === 'nl' ? 'Logistiek' : 'Logistics' },
  { value: 'energy', label: language === 'nl' ? 'Energie' : 'Energy' },
  { value: 'other', label: language === 'nl' ? 'Anders' : 'Other' },
];

const getCompanySizes = (language) => [
  { value: '1-50', label: '1-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-500', label: '201-500' },
  { value: '501-1000', label: '501-1000' },
  { value: '1001-5000', label: '1001-5000' },
  { value: '5000+', label: '5000+' },
];

// NEW: 6-point Likert scale (even number for clearer signal)
const getScoreLabels = (language) => language === 'nl' 
  ? ['Helemaal niet', 'Nauwelijks', 'In beperkte mate', 'In redelijke mate', 'In grote mate', 'Helemaal']
  : ['Not at all', 'Hardly', 'To a limited extent', 'To a reasonable extent', 'To a great extent', 'Fully'];

const getLevelLabel = (score, language) => {
  if (score <= 1.5) return language === 'nl' ? 'Beginnend' : 'Beginning';
  if (score <= 2.5) return language === 'nl' ? 'Opkomend' : 'Emerging';
  if (score <= 3.5) return language === 'nl' ? 'Ontwikkelend' : 'Developing';
  if (score <= 4.5) return language === 'nl' ? 'Gevorderd' : 'Advanced';
  if (score <= 5.5) return language === 'nl' ? 'Volwassen' : 'Mature';
  return language === 'nl' ? 'Leidend' : 'Leading';
};

export { getAssessmentDimensions, getIndustries, getCompanySizes, getScoreLabels, getLevelLabel };
